import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'market_map_theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

// Browser/PWA chrome colors, kept in sync with the media-scoped
// <meta name="theme-color"> tags in index.html. Those tags only track the OS
// preference; after a manual override that disagrees with the OS they go stale,
// so we drive a JS-managed meta from the effective theme instead.
const THEME_COLOR: Record<EffectiveTheme, string> = {
  light: '#FBF8F2',
  dark: '#1A1A18',
};

export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

export interface UseThemeResult {
  theme: ThemePreference;
  effectiveTheme: EffectiveTheme;
  toggle: () => void;
}

function readStoredTheme(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

function readSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function useTheme(): UseThemeResult {
  const [theme, setTheme] = useState<ThemePreference>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState<EffectiveTheme>(() => readSystemTheme());

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'system') {
      html.removeAttribute('data-theme');
    } else {
      html.setAttribute('data-theme', theme);
    }
  }, [theme]);

  const effectiveTheme: EffectiveTheme = theme === 'system' ? systemTheme : theme;

  // Sync the browser/PWA chrome color to the effective theme. Prepend a
  // JS-managed meta so it wins over index.html's media-scoped theme-color tags
  // (first matching meta in tree order wins), which stay wrong after a manual
  // override that disagrees with the OS preference.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let meta = document.head.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"][data-managed="true"]',
    );
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      meta.setAttribute('data-managed', 'true');
      document.head.insertBefore(meta, document.head.querySelector('meta[name="theme-color"]'));
    }
    meta.setAttribute('content', THEME_COLOR[effectiveTheme]);
  }, [effectiveTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const currentEffective: EffectiveTheme = prev === 'system' ? readSystemTheme() : prev;
      const next: ThemePreference = currentEffective === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // quota or disabled — ignore; data-theme attr is still applied.
      }
      return next;
    });
  }, []);

  return { theme, effectiveTheme, toggle };
}
